import type { ReactNode } from "react";
import { NavLink } from "react-router";
import { useTranslation } from "react-i18next";
import { useProject } from "@/api/hooks";
import type { Project } from "@/api/types/Project";
import { cx } from "@/components/ui";

export type AreaTab = { to: string; label: string; end?: boolean };

/**
 * A tab bar across the pages of one sidebar area (Bugs, Planning, Reports, Settings). The sidebar keeps one link
 * per area; the pages inside it are tabs here. `actions` sits on the right, in place of a page header's buttons.
 */
export function AreaTabs({ label, tabs, actions }: { label: string; tabs: AreaTab[]; actions?: ReactNode }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-x-3 gap-y-2 border-b border-border">
      <nav aria-label={label} className="-mb-px flex gap-1 overflow-x-auto">
        {tabs.map((tab) => (
          <NavLink
            key={tab.to}
            to={tab.to}
            end={tab.end}
            className={({ isActive }) =>
              cx(
                "border-b-2 px-3 py-2.5 font-medium whitespace-nowrap no-underline",
                isActive ? "border-accent font-semibold text-text" : "border-transparent text-muted hover:text-text",
              )
            }
          >
            {tab.label}
          </NavLink>
        ))}
      </nav>
      {actions && <div className="flex flex-wrap items-center gap-2 pb-2">{actions}</div>}
    </div>
  );
}

/** Epics and releases. */
export function PlanningTabs({ projectKey, actions }: { projectKey: string; actions?: ReactNode }) {
  const { t } = useTranslation();
  return (
    <AreaTabs
      label={t("nav.planning")}
      actions={actions}
      tabs={[
        { to: `/p/${projectKey}/epics`, label: t("epics.nav"), end: true },
        { to: `/p/${projectKey}/releases`, label: t("releases.nav"), end: true },
      ]}
    />
  );
}

/** Analytics, flow and the standup digest. */
export function ReportsTabs({ projectKey, actions }: { projectKey: string; actions?: ReactNode }) {
  const { t } = useTranslation();
  const project = useProject(projectKey);
  return (
    <AreaTabs
      label={t("nav.reports")}
      actions={actions}
      tabs={[
        { to: `/p/${projectKey}/analytics`, label: t("analytics.nav") },
        { to: `/p/${projectKey}/flow`, label: t("flow.nav") },
        ...(project.data?.scrum_enabled ? [{ to: `/p/${projectKey}/standup`, label: t("standup.nav") }] : []),
      ]}
    />
  );
}

/** The sections of project settings, each its own tab (`/settings/<section>`; General is `/settings`). */
export const SETTINGS_SECTIONS = ["workflow", "templates", "bugs", "apps", "channels", "gitlab"] as const;
export type SettingsSection = "general" | (typeof SETTINGS_SECTIONS)[number];

/** Project settings, split by topic, plus members. For managers; everyone else only has Members, so no tab bar. */
export function SettingsTabs({ project, actions }: { project: Project; actions?: ReactNode }) {
  const { t } = useTranslation();
  if (project.my_role !== "manager") return null;
  const base = `/p/${project.key}/settings`;
  return (
    <AreaTabs
      label={t("nav.settings")}
      actions={actions}
      tabs={[
        { to: base, label: t("settings.general"), end: true },
        { to: `${base}/workflow`, label: t("settings.workflow") },
        { to: `${base}/templates`, label: t("settings.templates") },
        { to: `${base}/bugs`, label: t("bugNav.bugs") },
        { to: `${base}/apps`, label: t("apps.title") },
        { to: `${base}/channels`, label: t("channels.title") },
        { to: `${base}/gitlab`, label: t("gitlabAdmin.title") },
        { to: `/p/${project.key}/members`, label: t("nav.members") },
      ]}
    />
  );
}
